import { useEffect } from "react";
import {
    Button,
    Checkbox,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
} from "@material-tailwind/react";
import {
    FormProvider,
    useForm,
    useFormContext,
} from "react-hook-form";
import { GobbyTable, GobbyTableHead, GobbyTableBody, GobbyTableRow, GobbyTableHeader, GobbyTableCell } from "components/ui/gobby-table";

// `roles` is an object map keyed by role id; checking a role writes the
// role NAME, unchecking writes undefined (the wire payload drops the key
// via JSON). The checked state is derived from the form value and written
// back with setValue on the canonical `roles.<id>` path; react-hook-form
// does not tolerate the `roles.[<id>]` variant.
function UserRoleCheckbox({ roleId, roleName }) {
    const { setValue, watch } = useFormContext();
    const assigned = watch("roles") || {};

    return (
        <Checkbox
            name="roles"
            type="checkbox"
            value={roleId}
            checked={!!assigned[roleId]}
            onChange={(e) => {
                setValue(
                    `roles.${roleId}`,
                    e.target.checked ? roleName : undefined,
                    { shouldDirty: true },
                );
            }}
        />
    );
}

// Lives inside the <Dialog> so every open remounts the form with fresh
// defaults from the current userData prop (recipe P5). userData can
// change identity mid-open (editingUser state on /organisation, stable
// per record), so the reset effect keys on that prop to re-baseline the
// form. There is no validation schema, so no resolver: isValid stays
// true.
function UserRolesForm({ userData, rolesList, handleUserSubmit, handleClose }) {
    const methods = useForm({
        mode: "onChange",
        defaultValues: { roles: userData?.roles || {} },
    });
    const { reset } = methods;
    // Read both flags up front: formState is a subscription proxy, so a
    // conditional read (`isValid && isDirty`) short-circuits before
    // isDirty while isValid is false. RHF then never subscribes to dirty
    // updates and isDirty stays frozen at false.
    const { isValid, isDirty } = methods.formState;

    useEffect(() => {
        reset({ roles: userData?.roles || {} }, { keepErrors: true });
    }, [userData, reset]);

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={methods.handleSubmit(async (values) => {
                    await handleUserSubmit(values);
                    handleClose();
                })}
            >
                <DialogBody className="dialog__body">
                    <Typography className="px-4 pt-2 font-body">
                        Assign roles to this user by selecting the checkboxes below.
                    </Typography>
                    {!!rolesList && (
                        <>
                            <div className=" bg-white rounded-xl p-2 mt-4">
                                <GobbyTable>
                                    <GobbyTableHead>
                                        <GobbyTableRow>
                                            <GobbyTableHeader className="w-5/6 pl-1 pt-4">
                                                Role
                                            </GobbyTableHeader>
                                            <GobbyTableHeader className="w-1/6 pt-4">
                                                Assign
                                            </GobbyTableHeader>
                                        </GobbyTableRow>
                                    </GobbyTableHead>
                                    <GobbyTableBody>
                                        {rolesList?.map(
                                            (role) => (
                                                <GobbyTableRow
                                                    key={role.id}
                                                >
                                                    <GobbyTableCell className="">
                                                        {role.name}
                                                    </GobbyTableCell>
                                                    <GobbyTableCell className="text-right md:pr-4">
                                                        <UserRoleCheckbox
                                                            roleId={role.id}
                                                            roleName={role.name}
                                                        />
                                                    </GobbyTableCell>
                                                </GobbyTableRow>
                                            ),
                                        )}
                                    </GobbyTableBody>
                                </GobbyTable>
                            </div>
                        </>
                    )}
                </DialogBody>
                <DialogFooter className="dialog__footer">
                    <div className="gap-2">
                        <Button
                            type="button"
                            className="btn btn--secondary"
                            variant="filled"
                            size="lg"
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!(isValid && isDirty)}
                            className="btn btn--primary"
                            variant="filled"
                            size="lg"
                        >
                            Save
                        </Button>
                    </div>
                </DialogFooter>
            </form>
        </FormProvider>
    );
}

export default function UserRolesDialog({
    showUserRoleDialog,
    setShowUserRoleDialog,
    handleUserSubmit,
    userData,
    rolesList,
}) {
    const handleClose = () => {
        setShowUserRoleDialog((showUserRoleDialog) => !showUserRoleDialog);
    };
    return (
        <Dialog
            open={showUserRoleDialog}
            handler={handleClose}
            className="dialog"
        >
            <DialogHeader className="dialog__header">
                Manage User Roles
            </DialogHeader>
            <UserRolesForm
                userData={userData}
                rolesList={rolesList}
                handleUserSubmit={handleUserSubmit}
                handleClose={handleClose}
            />
        </Dialog>
    );
}
